<template>
  <g
    :transform="`translate(${blockInstance.position_x}, ${blockInstance.position_y}) rotate(${blockInstance.rotation}, ${imageWidth / 2}, ${imageHeight / 2})`"
    class="block-instance"
    :class="{ selected }"
  >
    <!-- Image du bloc (WebP avec alpha ; plus de masque noir côté client) -->
    <image
      :href="displayImageUrl"
      :width="imageWidth"
      :height="imageHeight"
      x="0"
      y="0"
      class="block-image"
    />

    <!-- Contour et références devant l'image (ordre SVG = ordre de dessin) -->
    <rect
      :x="-5"
      :y="-5"
      :width="imageWidth + 10"
      :height="imageHeight + 10"
      fill="none"
      :stroke="selected ? '#4a9eff' : '#666'"
      stroke-width="selected ? 3 : 2"
      stroke-dasharray="5,5"
      :opacity="selected ? 0.8 : 0.6"
      class="block-outline"
    />

    <!-- Numéro d'inventaire en grand sur tout le bloc (mode layers) -->
    <g v-if="showZOrder" class="inventory-overlay">
      <rect
        :x="0"
        :y="0"
        :width="imageWidth"
        :height="imageHeight"
        fill="rgba(0,0,0,0.5)"
      />
      <text
        :x="imageWidth / 2"
        :y="imageHeight / 2"
        :font-size="inventoryFontSize"
        fill="white"
        text-anchor="middle"
        dominant-baseline="middle"
        class="inventory-label"
      >
        {{ inventoryLabel }}
      </text>
    </g>

    <!-- Zone cliquable (transparente, au-dessus pour garder les clics) -->
    <rect
      :x="0"
      :y="0"
      :width="imageWidth"
      :height="imageHeight"
      fill="white"
      class="block-clickable"
      @mousedown="handleMouseDown"
      style="opacity: 0; pointer-events: all;"
    />

    <!-- Debug: cadre vert pour visualiser la hitbox (seulement si sélectionné) -->
    <rect
      v-if="selected"
      :x="0"
      :y="0"
      :width="imageWidth"
      :height="imageHeight"
      fill="none"
      stroke="lime"
      stroke-width="2"
      style="pointer-events: none;"
    />

    <!-- Contrôles pour le bloc sélectionné -->
    <g v-if="selected" class="block-controls">
      <!-- Bouton verrouiller (en haut à gauche) -->
      <g class="lock-control">
        <circle
          :cx="lockBtnCx"
          :cy="-25"
          :r="controlRadiusUser"
          :fill="isLocked ? '#ff9800' : '#6c757d'"
          stroke="white"
          :stroke-width="controlStrokeUser"
          class="lock-btn"
          :title="isLocked ? 'Déverrouiller position et rotation' : 'Verrouiller position et rotation'"
          @mousedown.stop="handleLockClick"
        />
        <g
          :transform="`translate(${lockBtnCx}, -25)`"
          style="pointer-events: none;"
        >
          <svg
            :x="-controlIconSizeUser / 2"
            :y="-controlIconSizeUser / 2"
            :width="controlIconSizeUser"
            :height="controlIconSizeUser"
            viewBox="0 0 24 24"
          >
            <path
              v-if="isLocked"
              d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"
              fill="white"
            />
            <path
              v-else
              d="M12 17c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm6-9h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6h1.9c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1V8H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2z"
              fill="white"
            />
          </svg>
        </g>
      </g>
      <!-- Poignée de rotation (masquée si verrouillé ou si rotation gérée par le groupe) -->
      <g v-if="!isLocked && !hideRotationHandle" class="rotate-handle-group">
        <circle
          :cx="imageWidth / 2"
          :cy="-25"
          :r="controlRadiusUser"
          fill="#4a9eff"
          stroke="white"
          :stroke-width="controlStrokeUser"
          class="rotate-handle"
          @mousedown.stop="handleRotateStart"
        />
        <text
          :x="imageWidth / 2"
          :y="-25"
          :font-size="CONTROL_ICON_PX / pixelsPerUser"
          fill="white"
          text-anchor="middle"
          dominant-baseline="middle"
          pointer-events="none"
        >
          ↻
        </text>
        <!-- Indicateur d'angle avec fond pour meilleure lisibilité -->
        <g class="rotation-indicator">
          <rect
            :x="imageWidth / 2 - rotationIndicatorW / 2"
            :y="rotationIndicatorY"
            :width="rotationIndicatorW"
            :height="rotationIndicatorH"
            fill="rgba(26, 26, 26, 0.95)"
            stroke="#4a9eff"
            :stroke-width="1 / pixelsPerUser"
            :rx="2 / pixelsPerUser"
          />
          <text
            :x="imageWidth / 2"
            :y="rotationIndicatorTextY"
            :font-size="controlFontSizeUser"
            font-weight="600"
            fill="#4a9eff"
            text-anchor="middle"
            dominant-baseline="middle"
            pointer-events="none"
            :transform="`rotate(${-blockInstance.rotation}, ${imageWidth / 2}, ${rotationIndicatorTextY})`"
          >
            {{ Math.round(blockInstance.rotation) }}°
          </text>
        </g>
      </g>
      
      <!-- Bouton supprimer (en haut à droite) -->
      <g class="delete-control">
        <circle
          :cx="imageWidth + marginRightUser"
          :cy="-25"
          :r="controlRadiusUser"
          fill="#dc3545"
          stroke="white"
          :stroke-width="controlStrokeUser"
          class="delete-btn"
          @mousedown.stop="handleDeleteClick"
        />
        <!-- Icône poubelle en SVG (taille fixe, centrée dans le cercle) -->
        <g
          :transform="`translate(${imageWidth + marginRightUser}, -25)`"
          style="pointer-events: none;"
        >
          <svg
            :x="-controlIconSizeUser / 2"
            :y="-controlIconSizeUser / 2"
            :width="controlIconSizeUser"
            :height="controlIconSizeUser"
            viewBox="0 0 24 24"
          >
            <path
              d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
              fill="white"
            />
          </svg>
        </g>
      </g>

      <!-- Boutons z-order (empilés sous le bouton supprimer, espacement fixe en px) -->
      <g class="z-order-controls">
        <circle
          :cx="imageWidth + marginRightUser"
          :cy="-25 + controlGapUser"
          :r="controlRadiusUser"
          fill="#4a9eff"
          stroke="white"
          :stroke-width="controlStrokeUser"
          class="z-order-btn"
          @mousedown.stop="handleZOrderUp"
        />
        <text
          :x="imageWidth + marginRightUser"
          :y="-25 + controlGapUser"
          :font-size="CONTROL_ICON_PX / pixelsPerUser"
          fill="white"
          text-anchor="middle"
          dominant-baseline="middle"
          pointer-events="none"
        >
          ↑
        </text>
        
        <circle
          :cx="imageWidth + marginRightUser"
          :cy="-25 + 2 * controlGapUser"
          :r="controlRadiusUser"
          fill="#4a9eff"
          stroke="white"
          :stroke-width="controlStrokeUser"
          class="z-order-btn"
          @mousedown.stop="handleZOrderDown"
        />
        <text
          :x="imageWidth + marginRightUser"
          :y="-25 + 2 * controlGapUser"
          :font-size="CONTROL_ICON_PX / pixelsPerUser"
          fill="white"
          text-anchor="middle"
          dominant-baseline="middle"
          pointer-events="none"
        >
          ↓
        </text>

        <g v-if="hasComposite" class="composite-control">
          <circle
            :cx="imageWidth + marginRightUser"
            :cy="-25 + 3 * controlGapUser"
            :r="controlRadiusUser"
            fill="#2e7d32"
            stroke="white"
            :stroke-width="controlStrokeUser"
            class="composite-btn"
            @mousedown.stop="handleCompositeClick"
          />
          <g
            :transform="`translate(${imageWidth + marginRightUser}, ${-25 + 3 * controlGapUser})`"
            style="pointer-events: none;"
          >
            <svg
              :x="-controlIconSizeUser / 2"
              :y="-controlIconSizeUser / 2"
              :width="controlIconSizeUser"
              :height="controlIconSizeUser"
              viewBox="0 0 24 24"
            >
              <path
                d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"
                fill="white"
              />
            </svg>
          </g>
        </g>
      </g>
    </g>
  </g>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { getApiBase } from '@api/client';
import type { HypothesisBlock } from '@api/hypotheses.api';
import { useBlockDimensionsCache } from '../composables/useBlockDimensionsCache';
import { useBlockAlphaCache } from '../composables/useBlockAlphaCache';
import { getCachedBlockImage } from '../composables/useBlockImageCache';

const props = defineProps<{
  blockInstance: HypothesisBlock;
  selected: boolean;
  hideRotationHandle?: boolean;
  showZOrder?: boolean;
  svgRef?: SVGSVGElement | null;
  zoomLevel?: number;
  panOffset?: { x: number; y: number };
  canvasPixelsPerUser?: number;
}>();

const CONTROL_RADIUS_PX = 10;
const CONTROL_STROKE_PX = 1.5;
const MIN_GAP_PX = 6;
const CONTROL_ICON_PX = 12;
const pixelsPerUser = computed(() => Math.max(props.canvasPixelsPerUser ?? 1, 0.01));
const controlRadiusUser = computed(() => CONTROL_RADIUS_PX / pixelsPerUser.value);
const controlStrokeUser = computed(() => CONTROL_STROKE_PX / pixelsPerUser.value);
const controlFontSizeUser = computed(() => 10 / pixelsPerUser.value);
const controlIconSizeUser = computed(() => CONTROL_ICON_PX / pixelsPerUser.value);
const controlGapUser = computed(() => (2 * CONTROL_RADIUS_PX + MIN_GAP_PX) / pixelsPerUser.value);
const marginRightUser = computed(() => 16 / pixelsPerUser.value);
const lockBtnCx = computed(() => -(marginRightUser.value + 2 * controlRadiusUser.value));
const isLocked = computed(() => Boolean(props.blockInstance.locked));
const rotationIndicatorW = computed(() => 32 / pixelsPerUser.value);
const rotationIndicatorH = computed(() => 14 / pixelsPerUser.value);
const rotationIndicatorY = computed(() => -25 - controlRadiusUser.value - rotationIndicatorH.value - 4 / pixelsPerUser.value);
const rotationIndicatorTextY = computed(() => rotationIndicatorY.value + rotationIndicatorH.value / 2);

const emit = defineEmits<{
  select: [id: number, event?: MouseEvent];
  update: [id: number, updates: any, save?: boolean];
  'z-order-up': [id: number];
  'z-order-down': [id: number];
  delete: [id: number];
}>();

const router = useRouter();
const hasComposite = computed(() => Boolean(props.blockInstance.bloc?.composite_slug));
const apiBase = getApiBase();
const imagePath = computed(() => props.blockInstance.bloc.image_path);
const fullImageUrl = computed(() => `${apiBase}${imagePath.value}`);

/**
 * URL affichée dans le SVG. Initialement l'URL HTTP directe, remplacée par une
 * blob URL issue du cache dès que `loadImageDimensions` l'a résolue.
 */
const displayImageUrl = ref(fullImageUrl.value);
/** Blob URLs créées par le cache à révoquer à la destruction du composant. */
const ownedBlobUrls = ref<string[]>([]);

const dimensionsCache = useBlockDimensionsCache();
const blocId = computed(() => props.blockInstance.bloc?.id ?? props.blockInstance.bloc_id);
const blocUpdatedAt = computed(() => props.blockInstance.bloc?.updated_at ?? '');

function getInitialDimensions(): { width: number; height: number } {
  const id = blocId.value;
  if (id != null) {
    const cached = dimensionsCache.get(id, blocUpdatedAt.value);
    if (cached) return cached;
  }
  const w = props.blockInstance.bloc?.image_width;
  const h = props.blockInstance.bloc?.image_height;
  if (typeof w === 'number' && typeof h === 'number' && w > 0 && h > 0) {
    return { width: w, height: h };
  }
  return { width: 200, height: 200 };
}

const initial = getInitialDimensions();
const baseImageWidth = ref(initial.width);
const baseImageHeight = ref(initial.height);
const isDragging = ref(false);
const isRotating = ref(false);
const dragStart = ref({ x: 0, y: 0 });
const rotateStart = ref({ angle: 0, centerX: 0, centerY: 0 });
const pendingUpdates = ref<{ position_x?: number; position_y?: number; rotation?: number }>({});

const alphaCache = useBlockAlphaCache();

function isPixelOpaque(localX: number, localY: number): boolean {
  const result = alphaCache.isOpaque(blocId.value, localX, localY);
  return result ?? true; // fallback opaque si pas encore chargé
}

/**
 * Convertit des coordonnées SVG (dans le repère du canvas) en coordonnées locales
 * de l'image du bloc, en annulant la translation et la rotation du bloc.
 */
function svgToLocalImageCoords(svgX: number, svgY: number): { x: number; y: number } {
  const bx = props.blockInstance.position_x;
  const by = props.blockInstance.position_y;
  const rot = props.blockInstance.rotation ?? 0;
  const cx = imageWidth.value / 2;
  const cy = imageHeight.value / 2;
  // Annuler la translation
  const dx = svgX - bx;
  const dy = svgY - by;
  // Annuler la rotation autour du centre de l'image
  const rad = (-rot * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const lx = cos * (dx - cx) - sin * (dy - cy) + cx;
  const ly = sin * (dx - cx) + cos * (dy - cy) + cy;
  return { x: lx, y: ly };
}

// Taille canvas = pixels de l’image (exports déjà à l’échelle réelle) ; plus de scale_px_per_mm.
const imageWidth = computed(() => baseImageWidth.value);
const imageHeight = computed(() => baseImageHeight.value);

const showZOrder = computed(() => props.showZOrder ?? false);
const inventoryLabel = computed(
  () => props.blockInstance.bloc?.inventory_number ?? props.blockInstance.bloc?.object_name ?? '—'
);
const inventoryFontSize = computed(() =>
  Math.max(12, Math.min(imageWidth.value, imageHeight.value) / 5)
);

/** Ramène l'angle en degrés dans [0, 360) pour éviter accumulation 360°, 720°, etc. */
function normalizeAngleDeg(deg: number): number {
  const d = Number(deg) || 0;
  if (!Number.isFinite(d)) return 0;
  const r = d % 360;
  return r < 0 ? r + 360 : r;
}

const getSVGPoint = (clientX: number, clientY: number): { x: number; y: number } => {
  const svg = props.svgRef || (document.querySelector('.canvas-svg') as SVGSVGElement);
  if (!svg) {
    return { x: 0, y: 0 };
  }

  // Utiliser getScreenCTM qui gère automatiquement le viewBox
  const pt = svg.createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  const ctm = svg.getScreenCTM();
  if (!ctm) {
    return { x: 0, y: 0 };
  }
  const svgP = pt.matrixTransform(ctm.inverse());
  return { x: svgP.x, y: svgP.y };
};

const handleMouseDown = (event: MouseEvent) => {
  const target = event.target as SVGElement;
  
  // Ignorer les contrôles
  if (target.classList.contains('rotate-handle') ||
      target.classList.contains('z-order-btn') ||
      target.classList.contains('lock-btn') ||
      target.classList.contains('composite-btn') ||
      target.closest('.rotation-control') ||
      target.closest('.lock-control') ||
      target.closest('.composite-control')) {
    return;
  }

  // Test de transparence : ignorer le clic si le pixel est transparent
  const svgP = getSVGPoint(event.clientX, event.clientY);
  const local = svgToLocalImageCoords(svgP.x, svgP.y);
  if (!isPixelOpaque(local.x, local.y)) {
    return;
  }

  emit('select', props.blockInstance.id, event);
  event.preventDefault();
  event.stopPropagation();

  // Alt+clic sert à sélectionner un élément derrière sans démarrer de drag.
  if (event.altKey) return;
  if (isLocked.value) return;

  isDragging.value = true;
  dragStart.value = {
    x: svgP.x - props.blockInstance.position_x,
    y: svgP.y - props.blockInstance.position_y,
  };
};


const handleRotateStart = (event: MouseEvent) => {
  if (isLocked.value) return;
  isRotating.value = true;
  const centerX = Number(props.blockInstance.position_x) + Number(imageWidth.value) / 2;
  const centerY = Number(props.blockInstance.position_y) + Number(imageHeight.value) / 2;
  
  // Convertir la position de la souris en coordonnées SVG
  const mousePoint = getSVGPoint(event.clientX, event.clientY);
  
  const startAngle = Math.atan2(
    mousePoint.y - centerY,
    mousePoint.x - centerX
  );
  rotateStart.value = {
    angle: (startAngle * 180) / Math.PI - props.blockInstance.rotation,
    centerX,
    centerY,
  };
  event.preventDefault();
  event.stopPropagation();
};

const handleMouseMove = (event: MouseEvent) => {
  if (isDragging.value) {
    // Déplacer le bloc si on drag (le masque gère l'exclusion des zones noires)
    const svgP = getSVGPoint(event.clientX, event.clientY);
    const newX = svgP.x - dragStart.value.x;
    const newY = svgP.y - dragStart.value.y;
    
    // Stocker les updates sans sauvegarder
    pendingUpdates.value = {
      ...pendingUpdates.value,
      position_x: newX,
      position_y: newY,
    };
    
    // Mettre à jour visuellement (sans sauvegarder - 3e paramètre = false)
    emit('update', props.blockInstance.id, {
      position_x: newX,
      position_y: newY,
    }, false);
    event.preventDefault();
    event.stopPropagation(); // Empêcher le pan du canvas
  } else if (isRotating.value) {
    // Convertir la position de la souris en coordonnées SVG
    const mousePoint = getSVGPoint(event.clientX, event.clientY);
    const angle = Math.atan2(
      mousePoint.y - rotateStart.value.centerY,
      mousePoint.x - rotateStart.value.centerX
    );
    let newRotation = (angle * 180) / Math.PI - rotateStart.value.angle;
    
    // Si Shift est enfoncé, snap à l'angle multiple de 45° le plus proche
    if (event.shiftKey) {
      newRotation = Math.round(newRotation / 45) * 45;
    }
    newRotation = normalizeAngleDeg(newRotation);
    
    // Stocker les updates sans sauvegarder
    pendingUpdates.value = {
      ...pendingUpdates.value,
      rotation: newRotation,
    };
    
    // Mettre à jour visuellement (sans sauvegarder - 3e paramètre = false)
    emit('update', props.blockInstance.id, {
      rotation: newRotation,
    }, false);
    event.preventDefault();
    event.stopPropagation();
  }
};

const handleMouseUp = () => {
  const wasDragging = isDragging.value;
  const wasRotating = isRotating.value;
  
  if (isDragging.value) {
    isDragging.value = false;
  }
  if (isRotating.value) {
    isRotating.value = false;
  }
  
  // Sauvegarder seulement si on a des changements en attente (3e paramètre = true)
  if ((wasDragging || wasRotating) && Object.keys(pendingUpdates.value).length > 0) {
    emit('update', props.blockInstance.id, pendingUpdates.value, true);
    pendingUpdates.value = {};
  }
};

const handleZOrderUp = () => {
  emit('z-order-up', props.blockInstance.id);
};

const handleZOrderDown = () => {
  emit('z-order-down', props.blockInstance.id);
};

const handleDeleteClick = () => {
  emit('delete', props.blockInstance.id);
};

const handleLockClick = () => {
  emit('update', props.blockInstance.id, { locked: !isLocked.value }, true);
};

const handleCompositeClick = () => {
  if (!hasComposite.value) return;
  const id = props.blockInstance.bloc?.id ?? props.blockInstance.bloc_id;
  if (!id) return;
  router.push({ name: 'bloc-composite', params: { id: String(id) } });
};

// Dimensions natives de l'image servie (pas de plafond 300px : sinon toutes les tailles se confondent)
const loadImageDimensions = async () => {
  const path = imagePath.value;
  const updatedAt = props.blockInstance.bloc.updated_at ?? '';

  // Tenter de résoudre depuis le cache local (retourne une blob URL ou null)
  const blobUrl = await getCachedBlockImage(path, updatedAt);
  const urlToLoad = blobUrl ?? fullImageUrl.value;
  displayImageUrl.value = urlToLoad;

  if (blobUrl) {
    ownedBlobUrls.value.push(blobUrl);
  }

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = urlToLoad;

  await new Promise<void>((resolve) => {
    img.onload = () => {
      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      baseImageWidth.value = w;
      baseImageHeight.value = h;
      alphaCache.set(blocId.value, img);
      resolve();
    };
    img.onerror = () => {
      // Si la blob URL échoue, retomber sur l'URL directe
      if (blobUrl && img.src !== fullImageUrl.value) {
        displayImageUrl.value = fullImageUrl.value;
        img.src = fullImageUrl.value;
        return;
      }
      if (import.meta.env.DEV) {
        console.warn('[BlockInstance] Échec chargement image:', urlToLoad);
      }
      resolve();
    };
  });

  const id = blocId.value;
  if (id) {
    dimensionsCache.set(id, blocUpdatedAt.value, baseImageWidth.value, baseImageHeight.value);
    window.dispatchEvent(
      new CustomEvent('block-instance-dimensions', {
        detail: {
          blocId: id,
          blockId: props.blockInstance.id,
          width: imageWidth.value,
          height: imageHeight.value,
        },
      })
    );
  }
};

onMounted(() => {
  loadImageDimensions();
  window.addEventListener('mousemove', handleMouseMove);
  window.addEventListener('mouseup', handleMouseUp);
});

onUnmounted(() => {
  window.removeEventListener('mousemove', handleMouseMove);
  window.removeEventListener('mouseup', handleMouseUp);
  // Libérer les blob URLs créées par le cache pour éviter les fuites mémoire
  ownedBlobUrls.value.forEach((u) => URL.revokeObjectURL(u));
});
</script>

<style scoped>
.block-instance {
  cursor: move;
}

.block-instance.selected {
  cursor: move;
}

.block-image {
  pointer-events: none;
}

.block-clickable {
  cursor: move;
  pointer-events: all;
}


.rotate-handle {
  cursor: grab;
}

.rotate-handle:active {
  cursor: grabbing;
}

.block-outline {
  pointer-events: none;
}

.inventory-overlay {
  pointer-events: none;
}

.inventory-label {
  pointer-events: none;
  font-weight: 600;
  paint-order: stroke;
  stroke: rgba(0,0,0,0.8);
  stroke-width: 2px;
}

.z-order-controls {
  pointer-events: all;
}

.z-order-btn {
  cursor: pointer;
  transition: fill 0.2s;
}

.z-order-btn:hover {
  fill: #3a8eef;
}

.composite-control {
  pointer-events: all;
}

.composite-btn {
  cursor: pointer;
}

.delete-control {
  pointer-events: all;
}

.delete-btn {
  cursor: pointer;
  transition: fill 0.2s;
}

.delete-btn:hover {
  fill: #c82333;
}

.rotate-handle-group {
  pointer-events: all;
}

.rotation-indicator {
  font-family: monospace;
}

.rotation-control {
  pointer-events: all;
}

</style>
